import React, { useRef } from 'react';

import { makeStyles } from '@mui/styles';

const useStyles = makeStyles(() => ({
    progress: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        float: 'left',
        margin: '4px',
        textAlign: 'center'
    },
    barOverflow: {
        position: 'relative',
        height: '180px',
        marginBottom: '-104px',
        width: '360px'
    },
    bar: {
        transform: 'rotate(18deg) scaleX(-1) translate(-4%, 24%)',
        transformOrigin: '50% 50%',
        stroke: '#999DFFB2',
        strokeWidth: '8px',
        strokeLinecap: 'round'
    },
    barMuted: {
        stroke: '#D5D7E8'
    },
    barBackground: {
        transform: 'rotate(-35deg) scaleY(-1) scaleX(-1) translate(10%, -27%)',
        transformOrigin: '50% 50%',
        stroke: '#F3F3F3',
        strokeDasharray: 0,
        strokeDashoffset: 0,
        strokeLinecap: 'round',
        strokeWidth: '14px'
    },
    barBackgroundMuted: {
        stroke: '#E8EAF4'
    },
    remainderContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginTop: '18px'
    },
    amountContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
    },
    totalAmount: {
        fontWeight: 400
    },
    remainingAmount: {
        fontSize: '72px',
        fontWeight: 800,
        lineHeight: '77px'
    },
    remainingAmountMuted: {
        color: '#A6A6BF'
    },
    remainingText: {
        fontSize: '16px',
        fontWeight: 500,
        lineHeight: '22px'
    },
    remainingTextMuted: {
        color: '#A6A6BF'
    },
    limitText: {
        position: 'absolute',
        left: '85%',
        top: '84%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        fontWeight: 300
    },
    limitTextMuted: {
        color: '#A6A6BF'
    }
}));

type ProgressBarProps = {
    assignable?: boolean;
    limit: number;
    muted?: boolean;
    remaining?: number;
    used: number;
};

const ProgressBar: React.FC<ProgressBarProps> = ({
    assignable = true,
    limit,
    muted = false,
    remaining: remainingOverride,
    used
}) => {
    const classes = useStyles();
    const progressBarBackgroundRef = useRef(null);
    const progressBarRef = useRef(null);

    const monthlyRemaining = limit - used;
    const remaining = remainingOverride ?? monthlyRemaining;
    const progress = (used / limit) * 281;

    const renderRemainingContent = () => {
        if (remainingOverride === 0 && monthlyRemaining > 0) {
            return (
                <>
                    <span className={classes.remainingAmount}>$0</span>
                    <span className={classes.remainingText}>
                        Remaining to assign
                    </span>
                </>
            );
        }

        if (!assignable) {
            return (
                <>
                    <span
                        className={`${classes.remainingAmount} ${classes.remainingAmountMuted}`}
                    >
                        $0
                    </span>
                    <span
                        className={`${classes.remainingText} ${classes.remainingTextMuted}`}
                    >
                        Remaining to assign
                    </span>
                </>
            );
        }

        if (remaining > 0) {
            return (
                <>
                    <span
                        className={`${classes.remainingAmount} ${muted ? classes.remainingAmountMuted : ''}`}
                    >
                        ${remaining}
                    </span>
                    <span
                        className={`${classes.remainingText} ${muted ? classes.remainingTextMuted : ''}`}
                    >
                        Remaining to assign
                    </span>
                </>
            );
        } else {
            return (
                <>
                    <span className={classes.remainingAmount}>🎊</span>
                    <span
                        className={`${classes.remainingText} ${muted ? classes.remainingTextMuted : ''}`}
                    >
                        All Bonus Assigned!
                    </span>
                </>
            );
        }
    };

    return (
        <div className={classes.progress}>
            <div className={classes.barOverflow}>
                <svg
                    width="360"
                    height="200"
                    viewBox="0 0 220 120"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        ref={progressBarBackgroundRef}
                        className={`${classes.barBackground} ${muted ? classes.barBackgroundMuted : ''}`}
                        d="M 30,25 A 80,80 0 0 0 190,105"
                        fill="transparent"
                    />
                    <path
                        ref={progressBarRef}
                        className={`${classes.bar} ${muted ? classes.barMuted : ''}`}
                        style={
                            monthlyRemaining <= 0 && !muted
                                ? { stroke: '#E7FEEB' }
                                : {}
                        }
                        d="M 190,105 A 80,80 0 0 0 30,25"
                        fill="transparent"
                        strokeDasharray={2 * Math.PI * 80}
                        strokeDashoffset={503 - progress}
                    />
                </svg>
                <div
                    className={`${classes.limitText} ${muted ? classes.limitTextMuted : ''}`}
                >
                    <span>${limit}</span>
                    <span>limit</span>
                </div>
            </div>
            <div className={classes.remainderContainer}>
                <div className={classes.amountContainer}>
                    {renderRemainingContent()}
                </div>
            </div>
        </div>
    );
};

export default ProgressBar;
